import React, { useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";

import { removeProduct } from "../API/firebase";
import { describeFirebaseError } from "../lib/firebase-errors";
import { UserProductTypes } from "../models/types";
import { paths } from "../routes/paths";

export interface UserProductsDataGridProps {
  products: UserProductTypes[];
  /** Refetch after a delete, so the grid reflects what actually persisted. */
  onChanged: () => void;
}

const columns: GridColDef[] = [
  { field: "artome_product", headerName: "Product", flex: 1, maxWidth: 200 },
  {
    field: "artome_serial_no",
    headerName: "Artome serial #",
    flex: 1,
    maxWidth: 250,
  },
  {
    field: "epson_serial_no",
    headerName: "Projector serial #",
    flex: 1,
    maxWidth: 250,
  },
  {
    field: "setup_date",
    headerName: "Registered",
    flex: 1,
    minWidth: 140,
    maxWidth: 300,
  },
];

/**
 * Styling comes from the theme's `MuiDataGrid` overrides — the previous
 * hand-rolled dark wrapper is gone, along with its dark-grey-on-dark header bug.
 */
const UserProductsDataGrid: React.FC<UserProductsDataGridProps> = ({
  products,
  onChanged,
}) => {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      // Serials are the row ids, so the selection is directly usable.
      for (const serial of selection) {
        await removeProduct(String(serial));
      }
      setSelection([]);
      setDialogOpen(false);
      onChanged();
    } catch (err) {
      console.error("Device removal failed", err);
      setError(describeFirebaseError(err, "The removal"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <Button
          variant="contained"
          color="error"
          onClick={() => setDialogOpen(true)}
          disabled={selection.length === 0}
        >
          Remove selected
        </Button>
        {error && (
          <Box component="span" sx={{ color: "error.main", fontSize: 14 }}>
            {error}
          </Box>
        )}
      </Box>

      <DataGrid
        rows={products}
        columns={columns}
        getRowId={(row) => row.artome_serial_no}
        pageSizeOptions={[5, 10, 20]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
        }}
        checkboxSelection
        onRowSelectionModelChange={setSelection}
        rowSelectionModel={selection}
        // Keeps checkbox selection working while a row click opens the device.
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(paths.device(String(params.id)))}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Remove {selection.length} device(s)?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the registration records. It cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" disabled={deleting} autoFocus>
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProductsDataGrid;
